import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { api } from '../../lib/api'

interface Point { id: string; name: string; address?: string; lat?: number | null; lng?: number | null; geoRadiusM: number; isActive: boolean }
interface Bike { id: string; code: string; type?: string | null; status: string; notes?: string; tariff?: { name: string } | null }

type CreateForm = { name: string; address: string; lat: string; lng: string; geoRadiusM: number }
type EditForm = { name: string; address: string; lat: string; lng: string; geoRadiusM: number; isActive: boolean }
type EditBikeForm = { notes: string }

export default function PointsPage() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [selectedPoint, setSelectedPoint] = useState<Point | null>(null)
  const [editPoint, setEditPoint] = useState<Point | null>(null)
  const [editBike, setEditBike] = useState<Bike | null>(null)

  const { data } = useQuery({ queryKey: ['points'], queryFn: () => api.get('/points').then((r) => r.data) })
  const { data: bikesData, isLoading: bikesLoading } = useQuery({
    queryKey: ['bikes-at-point', selectedPoint?.id],
    queryFn: () => api.get('/bikes', { params: { pointId: selectedPoint!.id } }).then((r) => r.data),
    enabled: !!selectedPoint,
  })

  const createForm = useForm<CreateForm>({ defaultValues: { geoRadiusM: 200 } })
  const editPointForm = useForm<EditForm>()
  const editBikeForm = useForm<EditBikeForm>()

  const [createError, setCreateError] = useState<string | null>(null)

  function apiError(err: unknown): string {
    const e = err as { response?: { data?: { error?: { message?: string }; message?: string } } }
    return e?.response?.data?.error?.message ?? e?.response?.data?.message ?? 'Ошибка при сохранении'
  }

  const create = useMutation({
    mutationFn: (d: CreateForm) => api.post('/points', {
      name: d.name, address: d.address, geoRadiusM: d.geoRadiusM,
      ...(d.lat && d.lng ? { lat: parseFloat(d.lat), lng: parseFloat(d.lng) } : {}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['points'] }); createForm.reset(); setShowForm(false); setCreateError(null) },
    onError: (err) => setCreateError(apiError(err)),
  })

  const deactivatePoint = useMutation({
    mutationFn: (id: string) => api.patch(`/points/${id}`, { isActive: false }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['points'] }); setSelectedPoint(null) },
    onError: (err) => alert(apiError(err)),
  })

  const updatePoint = useMutation({
    mutationFn: ({ id, lat, lng, ...d }: EditForm & { id: string }) => api.patch(`/points/${id}`, {
      ...d,
      ...(lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : {}),
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['points'] })
      setEditPoint(null)
      if (selectedPoint?.id === res.data.point.id) setSelectedPoint(res.data.point)
    },
    onError: (err) => alert(apiError(err)),
  })

  const updateBike = useMutation({
    mutationFn: ({ id, ...d }: EditBikeForm & { id: string }) => api.patch(`/bikes/${id}`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bikes-at-point', selectedPoint?.id] }); setEditBike(null) },
  })

  const openEditPoint = (p: Point) => {
    setEditPoint(p)
    editPointForm.reset({
      name: p.name, address: p.address ?? '',
      lat: p.lat != null ? String(p.lat) : '',
      lng: p.lng != null ? String(p.lng) : '',
      geoRadiusM: p.geoRadiusM, isActive: p.isActive,
    })
  }

  const openEditBike = (b: Bike) => {
    setEditBike(b)
    editBikeForm.reset({ notes: b.notes ?? '' })
  }

  const statusColor: Record<string, string> = {
    free: 'bg-green-100 text-green-700',
    in_use: 'bg-blue-100 text-blue-700',
    reserved: 'bg-yellow-100 text-yellow-700',
    service: 'bg-gray-100 text-gray-600',
  }
  const statusLabel: Record<string, string> = { free: 'Свободен', in_use: 'В аренде', reserved: 'Забронирован', service: 'Сервис' }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Точки проката</h2>
        <button onClick={() => setShowForm(!showForm)} className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm font-medium">
          + Добавить
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
          {createError && <p className="mb-2 text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{createError}</p>}
          <form onSubmit={createForm.handleSubmit((d) => create.mutate(d))} className="space-y-3">
            <input {...createForm.register('name', { required: true })} placeholder="Название точки" className="w-full rounded-xl border-gray-300 text-sm" />
            <input {...createForm.register('address')} placeholder="Адрес" className="w-full rounded-xl border-gray-300 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input {...createForm.register('lat')} placeholder="Широта (55.7558)" className="rounded-xl border-gray-300 text-sm" />
              <input {...createForm.register('lng')} placeholder="Долгота (37.6173)" className="rounded-xl border-gray-300 text-sm" />
            </div>
            <p className="text-xs text-gray-400">Координаты — для гео-проверки открытия смены. Найдите точку в Яндекс.Картах → правый клик → «Что здесь».</p>
            <input {...createForm.register('geoRadiusM', { valueAsNumber: true })} type="number" defaultValue={200} placeholder="Радиус гео (м)" className="w-full rounded-xl border-gray-300 text-sm" />
            <div className="flex gap-2">
              <button type="submit" disabled={create.isPending} className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm disabled:opacity-60">Сохранить</button>
              <button type="button" onClick={() => setShowForm(false)} className="border border-gray-300 px-4 py-2 rounded-xl text-sm">Отмена</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {data?.points?.map((p: Point) => (
          <div key={p.id} className={`bg-white border rounded-2xl overflow-hidden ${selectedPoint?.id === p.id ? 'border-brand-400' : 'border-gray-200'}`}>
            {/* Point header — clickable */}
            <div className="p-4 cursor-pointer" onClick={() => setSelectedPoint(selectedPoint?.id === p.id ? null : p)}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{p.name}</h3>
                  <p className="text-sm text-gray-500">{p.address ?? '—'}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Радиус: {p.geoRadiusM} м
                    {p.lat && p.lng
                      ? <span className="text-green-600 ml-2">· 📍 Гео настроено</span>
                      : <span className="text-orange-500 ml-2">· Гео не задано</span>
                    }
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {p.isActive ? 'Активна' : 'Неактивна'}
                  </span>
                  <span className="text-gray-400">{selectedPoint?.id === p.id ? '▲' : '▼'}</span>
                </div>
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); openEditPoint(p) }}
                  className="text-xs text-brand-600 border border-brand-300 px-3 py-1 rounded-lg"
                >
                  Редактировать
                </button>
                {p.isActive && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (confirm(`Деактивировать точку «${p.name}»? Её нельзя будет открыть для смены.`)) {
                        deactivatePoint.mutate(p.id)
                      }
                    }}
                    className="text-xs text-red-600 border border-red-200 px-3 py-1 rounded-lg"
                  >
                    Деактивировать
                  </button>
                )}
              </div>
            </div>

            {/* Bikes at this point */}
            {selectedPoint?.id === p.id && (
              <div className="border-t border-gray-100">
                {bikesLoading ? (
                  <div className="p-4 text-center text-gray-400 text-sm">Загрузка велосипедов...</div>
                ) : !bikesData?.bikes?.length ? (
                  <div className="p-4 text-center text-gray-400 text-sm">Нет велосипедов на этой точке</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {bikesData.bikes.map((b: Bike) => (
                      <div key={b.id} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <span className="font-mono font-semibold text-sm">#{b.code}</span>
                          <span className="text-xs text-gray-400 ml-2">{b.tariff?.name ?? '—'}</span>
                          {b.notes && <p className="text-xs text-gray-400 mt-0.5">{b.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${statusColor[b.status] ?? 'bg-gray-100 text-gray-500'}`}>
                            {statusLabel[b.status] ?? b.status}
                          </span>
                          <button
                            onClick={() => openEditBike(b)}
                            className="text-xs text-brand-600 border border-brand-300 px-2 py-1 rounded-lg"
                          >
                            Изменить
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Point Modal */}
      {editPoint && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-end" onClick={() => setEditPoint(null)}>
          <div className="bg-white w-full rounded-t-3xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base">Редактировать точку</h3>
              <button onClick={() => setEditPoint(null)} className="text-gray-400 text-2xl leading-none">×</button>
            </div>
            <form onSubmit={editPointForm.handleSubmit((d) => updatePoint.mutate({ id: editPoint.id, ...d }))} className="space-y-3">
              <input {...editPointForm.register('name', { required: true })} placeholder="Название" className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              <input {...editPointForm.register('address')} placeholder="Адрес" className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              <div className="grid grid-cols-2 gap-2">
                <input {...editPointForm.register('lat')} placeholder="Широта" className="rounded-xl border-gray-300 text-sm border px-4 py-3" />
                <input {...editPointForm.register('lng')} placeholder="Долгота" className="rounded-xl border-gray-300 text-sm border px-4 py-3" />
              </div>
              <p className="text-xs text-gray-400">Координаты для гео-проверки смены (Яндекс.Карты → правый клик → «Что здесь»)</p>
              <input {...editPointForm.register('geoRadiusM', { valueAsNumber: true })} type="number" placeholder="Радиус (м)" className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              <label className="flex items-center gap-2 text-sm">
                <input {...editPointForm.register('isActive')} type="checkbox" /> Активна
              </label>
              <button type="submit" disabled={updatePoint.isPending} className="w-full bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
                {updatePoint.isPending ? 'Сохранение...' : 'Сохранить'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Bike Modal */}
      {editBike && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-end" onClick={() => setEditBike(null)}>
          <div className="bg-white w-full rounded-t-3xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base">Велосипед #{editBike.code}</h3>
              <button onClick={() => setEditBike(null)} className="text-gray-400 text-2xl leading-none">×</button>
            </div>
            <form onSubmit={editBikeForm.handleSubmit((d) => updateBike.mutate({ id: editBike.id, ...d }))} className="space-y-3">
              <p className="text-xs text-gray-500">Тариф и модель — управляйте в разделе «Велосипеды»</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Примечание</label>
                <input {...editBikeForm.register('notes')} placeholder="Состояние, особенности..." className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              </div>
              <button type="submit" disabled={updateBike.isPending} className="w-full bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
                {updateBike.isPending ? 'Сохранение...' : 'Сохранить'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
